import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { BalticService } from '@/services/balticService';
import type { BalticState, BalticRoute, BalticHistoryRequest } from '@/types/baltic.types';

interface BalticStore extends BalticState {
  // Actions
  fetchRoutes: (forceRefresh?: boolean) => Promise<void>;
  fetchHistoryData: (request: BalticHistoryRequest) => Promise<void>;
  setSelectedRoute: (route: BalticRoute | null) => void;
  setSelectedPeriod: (period: BalticHistoryRequest['period']) => void;
  setShowTCE: (showTCE: boolean) => void;
  refreshData: () => Promise<void>;
  historyLastUpdated: Date | null;
}

export const useBalticStore = create<BalticStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      routes: [],
      selectedRoute: null,
      historyData: [],
      selectedPeriod: 'daily',
      showTCE: false,
      loading: false,
      error: null,
      historyLoading: false,
      historyLastUpdated: null,

      // Actions
      fetchRoutes: async () => {
        set({ loading: true, error: null });
        try {
          const routes = await BalticService.getRoutes();
          const sortedRoutes = [...routes].sort((a, b) => {
            const aSort = typeof a.sort === 'number' ? a.sort : Number.NEGATIVE_INFINITY;
            const bSort = typeof b.sort === 'number' ? b.sort : Number.NEGATIVE_INFINITY;
            if (aSort !== bSort) return bSort - aSort;
            return a.route.localeCompare(b.route);
          });
          set({
            routes: sortedRoutes,
            loading: false,
            selectedRoute:
              get().selectedRoute || (sortedRoutes.length > 0 ? sortedRoutes[0] : null),
          });
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to fetch routes',
          });
        }
      },

      fetchHistoryData: async (request: BalticHistoryRequest) => {
        set({ historyLoading: true, error: null });

        try {
          const historyData = await BalticService.getHistoryData(request);
          // Sort by date ascending for chart display
          const sortedData = historyData.sort(
            (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
          );
          set({
            historyData: sortedData,
            historyLoading: false,
            selectedPeriod: request.period,
            historyLastUpdated: new Date(),
          });
        } catch (error) {
          set({
            historyLoading: false,
            error: error instanceof Error ? error.message : 'Failed to fetch history data',
          });
        }
      },

      setSelectedRoute: (route: BalticRoute | null) => {
        set({ selectedRoute: route });
        // Auto-fetch history for new route
        if (route) {
          const { selectedPeriod } = get();
          get().fetchHistoryData({ route: route.route, period: selectedPeriod });
        }
      },

      setSelectedPeriod: (period: BalticHistoryRequest['period']) => {
        set({ selectedPeriod: period });
        // Fetch history for selected route with new period
        const { selectedRoute } = get();
        if (selectedRoute) {
          get().fetchHistoryData({ route: selectedRoute.route, period });
        }
      },

      setShowTCE: (showTCE: boolean) => {
        set({ showTCE });
      },

      refreshData: async () => {
        await get().fetchRoutes();
      },
    }),
    { name: 'BalticStore' }
  )
);
